"use client";

/**
 * useDeleteCreation: the one way the web deletes a creation (founder
 * 2026-10-01: "deleting creations is not possible").
 *
 * One click, no confirm: Undo is the safety net. The creation leaves every
 * cached list that shows it at once (useSetDismissed), any preview open on it
 * closes, and a "Deleted" toast offers Undo, which restores it. The server's
 * delete is the soft `dismissed_at`, and a deleted creation is unreadable
 * through every door (/v, share links, grants, the box) until it is restored.
 *
 * `id` is the HEAD public id the UI holds: every list carries heads only, and
 * an older version resolves to the head on the server.
 */

import { useCallback } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/Toast";
import { useSetDismissed } from "@/components/studio-canvas/queries";
import type { TaskArtifact } from "@/components/studio-canvas/lineage";
import { useFilesStore } from "@/components/files-browser/filesStore";
import type { PreviewTarget } from "@/components/files-browser/preview-target";
import { useComputerStore } from "@/stores/computerStore";

/** Long enough to reach for Undo; the toast also holds while hovered. */
const UNDO_WINDOW_MS = 6_000;

/** The row as the lists hold it now, so Undo can put it straight back. */
function cachedCreation(
  queryClient: QueryClient,
  id: string
): TaskArtifact | undefined {
  for (const queryKey of [
    ["user-artifacts"],
    ["task-artifacts"],
    ["media-artifacts"],
  ]) {
    for (const [, data] of queryClient.getQueriesData({ queryKey })) {
      const list = Array.isArray(data)
        ? (data as TaskArtifact[])
        : (data as { artifacts?: TaskArtifact[] } | undefined)?.artifacts;
      const hit = list?.find((a) => a.id === id);
      if (hit) return hit;
    }
  }
  return undefined;
}

/** Close the overlay, and any task's Preview tab, showing this creation. */
function closePreviewsOf(id: string): void {
  const showsIt = (t: PreviewTarget | null | undefined) =>
    t?.share?.lane === "artifact" && t.share.id === id;
  const files = useFilesStore.getState();
  if (showsIt(files.previewTarget)) files.closePreview();
  const computer = useComputerStore.getState();
  for (const [taskId, slot] of Object.entries(computer.byTask)) {
    if (showsIt(slot?.previewTarget)) computer.closePreview(taskId);
  }
}

export function useDeleteCreation(): (id: string) => void {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutateAsync } = useSetDismissed();
  return useCallback(
    (id: string) => {
      const artifact = cachedCreation(queryClient, id);
      closePreviewsOf(id);
      // mutateAsync, not mutate's callbacks: the tile that asked unmounts the
      // moment its row leaves the list, and the answer must still be heard.
      // The row leaves the lists at once (optimistic); "Deleted" waits for the
      // server, so a refused delete never shows "Deleted" beside "Couldn't delete".
      mutateAsync({ id, dismissed: true }).then(
        () =>
          toast("Deleted", {
            durationMs: UNDO_WINDOW_MS,
            action: {
              label: "Undo",
              onClick: () => {
                mutateAsync({ id, dismissed: false, artifact }).catch(() =>
                  toast("Couldn't undo. Try again.")
                );
              },
            },
          }),
        () => toast("Couldn't delete. Try again.")
      );
    },
    [queryClient, toast, mutateAsync]
  );
}
